<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue';
import { $api } from '@/services';
import { useStore } from 'vuex';

const props = defineProps({
	menuId: {
		type: String,
		required: true,
	},
	startDate: {
		required: true,
	},
});
const isExist = ref(false);
const store = useStore();
const week = ref(null);
const emit = defineEmits(['success']);
const minDate = computed(() => {
	const today = new Date();
	const day = today.getDay();
	const diff = (day - 1 + 7) % 7;

	const startOfWeek = new Date(today);
	startOfWeek.setDate(today.getDate() - diff);
	startOfWeek.setHours(0, 0, 0, 0);

	return startOfWeek;
});

function isSameWeek(date1, date2) {
	const d1 = new Date(date1);
	const d2 = new Date(date2);

	const getStartOfWeek = (d) => {
		const day = d.getDay();
		const diff = (day - 1 + 7) % 7;
		const res = new Date(d);
		res.setDate(d.getDate() - diff);
		res.setHours(0, 0, 0, 0);
		return res.getTime();
	};

	return getStartOfWeek(d1) === getStartOfWeek(d2);
}

const disabledDates = (date) => {
	return isSameWeek(date, props.startDate);
};

function toLocalDateString(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

async function submit() {
	try {
		const res = await $api.menu.checkExistWeekMenu({
			week_start: toLocalDateString(week.value[0]),
		});
		if (res.data?.data?.exists) {
			isExist.value = true;
			return;
		}
		duplicateWeekMenu();
	} catch (error) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
}

const duplicateWeekMenu = async () => {
	try {
		await $api.menu.duplicateWeekMenu(props.menuId, {
			week_start: toLocalDateString(week.value[0]),
		});
		emit('success');
		await store.dispatch('setAlertMessage', {
			message: 'Tạo bản sao thành công',
			type: 'success',
		});
	} catch (error) {
		if (error?.response?.data?.errors.length > 0) {
			await store.dispatch('setAlertMessage', {
				message:
					error?.response?.data?.errors.join('<br/>'),
				type: 'danger',
			});
		} else {
			await store.dispatch('setAlertMessage', {
				message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
				type: 'danger',
			});
		}
	}
};
</script>
<template>
	<div class="_create-boarding _modal bg-white">
		<div
			class="_modal__content px-12 py-6 flex flex-col items-center justify-center"
		>
			<div>
				<img
					v-if="isExist"
					src="@/assets/images/illustrations/warning.png"
					alt="warning"
					width="60"
					height="60"
				/>
				<img
					v-else
					alt="Luca Education"
					src="@/assets/images/illustrations/duplicate.png"
				/>
			</div>
			<div
				class="_modal__content__title text-neutral-700 font-semibold text-2xl mt-5 text-center"
			>
				{{ isExist ? 'Tuần bạn chọn đã có thực đơn' : 'Tạo bản sao' }}
			</div>
			<div class="text-center text-neutral-500 mt-2">
				{{
					isExist
						? 'Nếu tiếp tục, thực đơn hiện tại sẽ bị ghi đè và không thể khôi phục'
						: 'Chọn tuần bạn muốn áp dụng thực đơn'
				}}
			</div>
			<div class="flex items-center gap-5 mt-4 w-full" v-if="!isExist">
				<div
					class="text-neutral-400 font-semibold text-xs whitespace-nowrap"
				>
					Thực đơn tuần
				</div>
				<VueDatepicker
					class="input-date w-full"
					placeholder="Chọn tuần"
					autoApply
					v-model="week"
					format="dd/MM/yyyy"
					:min-date="minDate"
					:disabled-dates="disabledDates"
					:week-start="1"
					week-picker
				>
					<template #input-icon>
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M5 0.625C5 0.279822 4.72018 0 4.375 0C4.02982 0 3.75 0.279822 3.75 0.625V1.25H2.5C1.11929 1.25 0 2.36929 0 3.75V5H20V3.75C20 2.36929 18.8807 1.25 17.5 1.25H16.25V0.625C16.25 0.279822 15.9702 0 15.625 0C15.2798 0 15 0.279822 15 0.625V1.25H5V0.625ZM20 17.5V6.25H0V17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5ZM11.875 8.75C11.5298 8.75 11.25 9.02982 11.25 9.375V10.625C11.25 10.9702 11.5298 11.25 11.875 11.25H13.125C13.4702 11.25 13.75 10.9702 13.75 10.625V9.375C13.75 9.02982 13.4702 8.75 13.125 8.75H11.875ZM15.625 8.75C15.2798 8.75 15 9.02982 15 9.375V10.625C15 10.9702 15.2798 11.25 15.625 11.25H16.875C17.2202 11.25 17.5 10.9702 17.5 10.625V9.375C17.5 9.02982 17.2202 8.75 16.875 8.75H15.625ZM2.5 13.125C2.5 12.7798 2.77982 12.5 3.125 12.5H4.375C4.72018 12.5 5 12.7798 5 13.125V14.375C5 14.7202 4.72018 15 4.375 15H3.125C2.77982 15 2.5 14.7202 2.5 14.375V13.125ZM6.875 12.5C6.52982 12.5 6.25 12.7798 6.25 13.125V14.375C6.25 14.7202 6.52982 15 6.875 15H8.125C8.47018 15 8.75 14.7202 8.75 14.375V13.125C8.75 12.7798 8.47018 12.5 8.125 12.5H6.875Z"
								fill="#94A3B8"
							/>
						</svg>
					</template>
				</VueDatepicker>
			</div>
		</div>
		<div
			class="_modal__footer px-12 py-6 bg-neutral-50 flex flex-nowrap items-center"
		>
			<button
				class="button-p text-center mr-3 bg-none shadow-none text-neutral-500 hover:text-neutral-700"
				type="button"
				@click="$emit('close')"
			>
				Huỷ
			</button>
			<button
				v-if="isExist"
				class="button-p text-center whitespace-nowrap"
				type="button"
				@click="duplicateWeekMenu"
			>
				Ghi đè thực đơn
			</button>
			<button
				v-else
				class="button-p text-center whitespace-nowrap"
				type="button"
				@click="submit"
				:disabled="!week"
			>
				Xác nhận
			</button>
		</div>
	</div>
</template>
